import {
  KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT,
  KUSTOMER_APP_CHAT_CSAT_SEND_EVENT,
  KUSTOMER_APP_CHAT_CSAT_SUBMIT_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT,
} from 'core_main/pubnub/constants';

import {
  SatisfactionCallbackResponse,
  SatisfactionForm,
  SatisfactionResponse,
} from 'core_main/api/satisfaction/types';
import { createSatisfactionCallbackResponse } from 'core_main/api/satisfaction/helpers';
import { ChatMessagesData } from 'core_main/api/messages/types';
import Pubnub from 'pubnub';
import { getStore } from 'core_main/store';
import { parseUrlUnfurled } from 'core_main/pubnub/helpers/parseUrlUnfurled';
import { UrlUnfurled } from 'core_main/pubnub/types/urlUnfurled';
import { createMessageCallbackResponse } from '../send_message/helpers';
import { GetMessagesCallbackResponse, PNMessagesListMessage } from './types';
import { MessageCallbackResponse } from '../send_message/types';

/**
 * Create callback response from PubNub messages including URL previews
 *
 * Processes PubNub history messages and attaches URL previews to their associated messages.
 * Uses a two-pass approach:
 * 1. First pass: Collects regular messages and URL unfurled events separately
 * 2. Second pass: Attaches URL previews to messages by originalMessageId
 *
 * Handles both inbound and outbound URL unfurled events:
 * - `kustomer.app.chat.url.unfurled.send` (outbound agent messages)
 * - `kustomer.app.chat.url.unfurled.receive` (inbound user messages)
 *
 * CRITICAL: URL unfurled events from history do NOT affect unread counts, delivery status,
 * or read tracking. They are purely visual enhancements to existing messages.
 *
 * Features:
 * - Prevents duplicate previews by checking URL before adding
 * - Updates both messagesMap and history.messages array
 * - Handles messages that may not have associated previews
 * - Processes satisfaction responses if present
 *
 * @param pubnubMessages - Array of PubNub messages from history (may include URL unfurled events)
 * @param sessionId - Conversation/session ID to associate with the response
 * @returns GetMessagesCallbackResponse containing messages with previews attached and satisfaction data
 *
 * @example
 * ```typescript
 * const response = createGetMessagesCallbackResponse(pubnubHistory, 'session-123');
 * // response.messages contains messages with unfurledPreviews attached
 * ```
 */
export const createGetMessagesCallbackResponse = (
  pubnubMessages: PNMessagesListMessage[] | undefined,
  sessionId,
): GetMessagesCallbackResponse => {
  const history = {
    satisfaction: {} as SatisfactionCallbackResponse,
    messages: [] as MessageCallbackResponse[],
  };
  const urlUnfurledEvents: UrlUnfurled[] = [];
  const messagesMap = new Map<string, MessageCallbackResponse>();
  
  // Check if URL unfurling is enabled
  const { enableUrlUnfurling } = getStore();

  if (pubnubMessages?.length) {
    // First pass: collect messages and URL unfurled events
    pubnubMessages.forEach((data) => {
      if (
        data.message.name === KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT ||
        data.message.name === KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT
      ) {
        const message = createMessageCallbackResponse(
          data.message.data as ChatMessagesData,
          data?.attachments?.data,
          data?.message?.included,
          'pubnub',
        );
        messagesMap.set(message.messageId, message);
        history.messages.push(message);
      }

      if (
        data.message.name === KUSTOMER_APP_CHAT_CSAT_SEND_EVENT ||
        data.message.eventType === KUSTOMER_APP_CHAT_CSAT_SUBMIT_EVENT
      ) {
        const form = data?.message.included?.find(
          (entity) => entity.type === 'satisfaction',
        ) as SatisfactionForm;
        const { timetoken } = data;
        history.satisfaction = createSatisfactionCallbackResponse(
          data?.message?.data as SatisfactionResponse,
          timetoken,
          form,
        );
      }

      // Collect URL unfurled events (only if URL unfurling is enabled)
      if (
        enableUrlUnfurling &&
        (data.message.name === KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT ||
          data.message.name === KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT)
      ) {
        // PubNub message structure for URL unfurled events
        const urlUnfurled = parseUrlUnfurled(data.message as Pubnub.MessageEvent['message']);
        if (urlUnfurled) {
          urlUnfurledEvents.push(urlUnfurled);
        }
      }
    });

    // Second pass: attach URL previews to messages
    urlUnfurledEvents.forEach((urlUnfurled) => {
      const { originalMessageId } = urlUnfurled;
      const message = messagesMap.get(originalMessageId);

      if (message) {
        const currentPreviews = message.unfurledPreviews || [];
        const { url: newPreviewUrl } = urlUnfurled.unfurledUrl;
        const alreadyExists = currentPreviews.some((preview) => preview.url === newPreviewUrl);

        if (!alreadyExists) {
          // Update message with new preview
          const updatedMessage: MessageCallbackResponse = {
            ...message,
            unfurledPreviews: [...currentPreviews, urlUnfurled.unfurledUrl],
          };
          messagesMap.set(originalMessageId, updatedMessage);
          // Update in history array
          const index = history.messages.findIndex((m) => m.messageId === originalMessageId);
          if (index !== -1) {
            history.messages[index] = updatedMessage;
          }
        }
      }
    });
  }

  return {
    conversationId: sessionId,
    ...history,
  };
};
